/**
 * TaskListByEntity
 * Lista de tarefas genérica para qualquer entidade (cliente, caso, atendimento)
 */

import { useState, useEffect, useCallback } from 'react';
import { Task, TaskStatus, TaskPriority } from '@/types/tasks';
import { taskService } from '@/services/task-service';
import { TaskCard } from './TaskCard';
import { useTaskDrawer } from '@/contexts/TaskDrawerContext';
import { Filter, SortAsc, Loader2, AlertCircle } from 'lucide-react';
import toast from '@/lib/toast';
import { isTaskOverdue, parseTaskDueDate } from '@/utils/task-date';
import { parseDbTimestamp } from '@/utils/datetime';
import { NativeSelect } from "@/components/ui/native-select"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"

import { getErrorMessage } from '../../utils/error.utils'
export interface TaskListByEntityProps {
  entityType: 'client' | 'case' | 'interaction';
  entityId: string;
  showClient?: boolean;
  showCase?: boolean;
  showFilters?: boolean;
  /** Sem os contadores (página do caso: só o essencial). */
  showStats?: boolean;
  onTaskClick?: (task: Task) => void;
  refreshTrigger?: number; // Increment this to trigger refresh
}

export function TaskListByEntity({
  entityType,
  entityId,
  showClient = false,
  showCase = false,
  showFilters = true,
  showStats = true,
  onTaskClick,
  refreshTrigger
}: TaskListByEntityProps) {
  const drawer = useTaskDrawer();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'ALL'>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'ALL'>('ALL');
  const [sortBy, setSortBy] = useState<'due_date' | 'priority' | 'created_at'>('due_date');

  // Load tasks
  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let response;

      if (entityType === 'client') {
        response = await taskService.getByClient(entityId);
      } else if (entityType === 'case') {
        response = await taskService.getByCase(entityId);
      } else if (entityType === 'interaction') {
        response = await taskService.getByInteraction(entityId);
      }

      if (response && response.data) {
        setTasks(response.data.tasks || []);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar tarefas'));
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks, refreshTrigger, drawer.version]);

  // Handle status change
  const handleStatusChange = async (taskId: number, newStatus: string) => {
    try {
      if (newStatus === 'COMPLETED') {
        await taskService.complete(taskId);
      } else {
        await taskService.update(taskId, { status: newStatus as TaskStatus });
      }

      toast.success('Status atualizado');
      loadTasks(); // Refresh list
    } catch (err: unknown) {
      toast.error('Erro ao atualizar status da tarefa');
    }
  };

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    if (statusFilter !== 'ALL' && task.status !== statusFilter) return false;
    if (priorityFilter !== 'ALL' && task.priority !== priorityFilter) return false;
    return true;
  });

  // Sort tasks
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (sortBy === 'due_date') {
      const aDate = parseTaskDueDate(a.due_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bDate = parseTaskDueDate(b.due_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return aDate - bDate;
    } else if (sortBy === 'priority') {
      const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    } else if (sortBy === 'created_at') {
      const bTime = parseDbTimestamp(b.created_at)?.getTime() ?? 0;
      const aTime = parseDbTimestamp(a.created_at)?.getTime() ?? 0;
      return bTime - aTime;
    }
    return 0;
  });

  // Count by status
  const statusCounts = {
    all: tasks.length,
    pending: tasks.filter(t => t.status === 'PENDING').length,
    in_progress: tasks.filter(t => t.status === 'IN_PROGRESS').length,
    for_review: tasks.filter(t => t.status === 'FOR_REVIEW').length,
    completed: tasks.filter(t => t.status === 'COMPLETED').length,
    overdue: tasks.filter(isTaskOverdue).length
  };

  const stats: Array<[string, number, string?]> = [
    ['Total', statusCounts.all],
    ['Pendentes', statusCounts.pending],
    ['Em Andamento', statusCounts.in_progress],
    ['Em Revisão', statusCounts.for_review],
    ['Concluídas', statusCounts.completed],
    ['Atrasadas', statusCounts.overdue, statusCounts.overdue > 0 ? 'text-destructive' : undefined],
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" aria-busy="true">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label="Carregando tarefas" />
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
        <AlertCircle className="mx-auto mb-2 size-5 text-destructive" aria-hidden="true" />
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={loadTasks}>Tentar novamente</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {showStats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          {stats.map(([label, value, tone]) => (
            <div key={label} className="rounded-lg border bg-card p-3 text-center">
              <div className={`text-2xl font-semibold tabular-nums ${tone ?? ''}`}>{value}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      )}

      {showFilters && (
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Filter className="size-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium">Filtros</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${entityType}-tasks-status`} className="text-xs">Status</Label>
              <NativeSelect
                id={`${entityType}-tasks-status`}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TaskStatus | 'ALL')}
              >
                <option value="ALL">Todos</option>
                <option value="PENDING">Pendentes</option>
                <option value="IN_PROGRESS">Em Andamento</option>
                <option value="FOR_REVIEW">Em Revisão</option>
                <option value="COMPLETED">Concluídas</option>
                <option value="CANCELLED">Canceladas</option>
              </NativeSelect>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${entityType}-tasks-priority`} className="text-xs">Prioridade</Label>
              <NativeSelect
                id={`${entityType}-tasks-priority`}
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | 'ALL')}
              >
                <option value="ALL">Todas</option>
                <option value="URGENT">Urgente</option>
                <option value="HIGH">Alta</option>
                <option value="MEDIUM">Média</option>
                <option value="LOW">Baixa</option>
              </NativeSelect>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${entityType}-tasks-sort`} className="text-xs">
                <SortAsc className="size-3" aria-hidden="true" /> Ordenar por
              </Label>
              <NativeSelect
                id={`${entityType}-tasks-sort`}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              >
                <option value="due_date">Data de vencimento</option>
                <option value="priority">Prioridade</option>
                <option value="created_at">Mais recentes</option>
              </NativeSelect>
            </div>
          </div>
        </div>
      )}

      {sortedTasks.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 py-10 text-center">
          <p className="text-sm text-muted-foreground">Nenhuma tarefa encontrada</p>
          {statusFilter !== 'ALL' || priorityFilter !== 'ALL' ? (
            <Button
              variant="link"
              size="sm"
              className="mt-1"
              onClick={() => {
                setStatusFilter('ALL');
                setPriorityFilter('ALL');
              }}
            >
              Limpar filtros
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sortedTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => (onTaskClick ? onTaskClick(task) : drawer.openTask(task.id))}
              onStatusChange={handleStatusChange}
              showClient={showClient}
              showCase={showCase}
            />
          ))}
        </div>
      )}
    </div>
  );
}
